import { useEffect, useState } from "react";

import { useTabVisibility } from "@/hooks/useTabVisibility";

/**
 * A single shared ticker that drives every "Updated N min ago" label
 * in the app. Centralising this prevents per-component intervals from
 * drifting apart and duplicating work.
 *
 * The cadence is adaptive:
 *   • Tab hidden → no ticks at all.
 *   • `boost = true` (popover open, hover in progress) → 15s ticks so
 *     the timestamp feels live to whoever is looking at it.
 *   • Otherwise → 60s base cadence. Matches Webull / Wealthfront /
 *     Google Finance; good enough for "N min ago" granularity.
 *
 * The ticker is NOT the fresh/stale decider on its own — consumers
 * compute `(now - fetchedAt) < FRESHNESS_WINDOW_MS` on each render
 * using this `now`. Letting the ticker tell you "now" without reading
 * it on every render is fine; letting the ticker tell you "fresh or
 * stale" independently would duplicate state.
 *
 * Uses recursive `setTimeout` instead of `setInterval` so a tab that
 * was hidden and came back fires the next tick immediately (the
 * visibility listener re-mounts the scheduler), and so wake-from-
 * sleep doesn't accumulate a backlog of missed ticks.
 */
export function useSharedNow(opts?: { boost?: boolean }): Date {
  const boost = opts?.boost ?? false;
  const visibility = useTabVisibility();
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    if (visibility === "hidden") {
      // Nothing running. On return to visible, the effect re-runs and
      // pushes a fresh `now` so consumers don't render an aged value.
      return;
    }
    // Immediate write on effect-mount so a resumed tab surfaces the
    // correct time before the next scheduled tick.
    setNow(new Date());
    const intervalMs = boost ? 15_000 : 60_000;
    let cancelled = false;
    let timeoutId: number | null = null;
    const tick = () => {
      if (cancelled) return;
      setNow(new Date());
      timeoutId = window.setTimeout(tick, intervalMs);
    };
    timeoutId = window.setTimeout(tick, intervalMs);
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [visibility, boost]);

  return now;
}
